import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { ArrowLeft, FileBarChart, Download, Trash2, Loader2, Calendar, CalendarRange, CalendarDays, Filter, Sparkles, Mail, Send } from "lucide-react";
import { toast } from "sonner";
import { PAYMENT_METHOD_OPTIONS, PAYMENT_METHOD_LABELS } from "@/components/RecordPaymentDialog";

const MONTH_NAMES = ["", "January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const money = (v) => `$${(Number(v || 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Tax-ready preset per PRD 2026-02: hides Cash + Cash App + Venmo (peer-to-peer)
// so the resulting yearly/quarterly statement matches the traditional
// "reported income" view accountants expect. Never bakes into the doc unless
// the user actually generates a statement with these chips on.
const TAX_READY_EXCLUDES = ["cash", "cash_app", "venmo"];

const statementLabel = (s) => {
  if (s.period === "yearly") {
    return `${s.year}${s.data?.end && new Date(s.data.end).getFullYear() === s.year && new Date(s.data.end) < new Date(`${s.year}-12-31`) ? " · YTD" : ""}`;
  }
  if (s.period === "quarterly") {
    return `Q${s.quarter || "?"} ${s.year}`;
  }
  return `${MONTH_NAMES[s.month] || "?"} ${s.year}`;
};

export default function Statements() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const now = new Date();
  const currentQuarter = Math.floor(now.getMonth() / 3) + 1;
  const [genYear, setGenYear] = useState(now.getFullYear());
  const [genMonth, setGenMonth] = useState(now.getMonth() + 1);
  const [genQuarter, setGenQuarter] = useState(currentQuarter);
  const [busy, setBusy] = useState(false);
  const [excluded, setExcluded] = useState([]);
  const [emailOpen, setEmailOpen] = useState(false);
  const token = typeof window !== "undefined" ? localStorage.getItem("lf_token") : "";

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/financials/statements");
      setItems(data);
      if (data.length && !selected) setSelected(data[0]);
    } catch { toast.error("Failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const excludeQs = () => excluded.length ? `&exclude=${excluded.join(",")}` : "";

  const generateMonthly = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/financials/statements/generate?year=${genYear}&month=${genMonth}${excludeQs()}`);
      toast.success(`${MONTH_NAMES[genMonth]} ${genYear} generated`);
      setSelected(data);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const generateQuarterly = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/financials/statements/generate-quarterly?year=${genYear}&quarter=${genQuarter}${excludeQs()}`);
      toast.success(`Q${genQuarter} ${genYear} generated`);
      setSelected(data);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const generateYearly = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/financials/statements/generate-yearly?year=${genYear}${excludeQs()}`);
      const label = genYear === now.getFullYear() ? `${genYear} YTD` : `${genYear}`;
      toast.success(`${label} yearly statement generated`);
      setSelected(data);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const applyTaxPreset = () => {
    // Preselect Tax-Ready exclusions AND jump the generator to Yearly (YTD)
    // for the most common accountant handoff — the actual generation still
    // requires an explicit click on "Yearly (YTD)".
    setExcluded(TAX_READY_EXCLUDES);
    toast.success("Tax-Ready preset applied — Cash, Cash App and Venmo will be excluded. Click Yearly (YTD) to generate.");
  };

  const del = async (id) => {
    if (!window.confirm("Delete this statement?")) return;
    try { await api.delete(`/financials/statements/${id}`); if (selected?.id === id) setSelected(null); load(); }
    catch { toast.error("Failed"); }
  };

  const pdfUrl = (id) => {
    const root = API.replace(/\/api$/, "");
    return `${root}/api/financials/statements/${id}/pdf?auth=${encodeURIComponent(token || "")}`;
  };

  const toggleExclude = (m) => {
    setExcluded((prev) => prev.includes(m) ? prev.filter((x) => x !== m) : [...prev, m]);
  };

  const years = Array.from({length: 5}, (_, i) => now.getFullYear() - i);
  const isYearly = selected?.period === "yearly";
  const isQuarterly = selected?.period === "quarterly";
  const isTaxPresetActive = TAX_READY_EXCLUDES.every((m) => excluded.includes(m)) && excluded.length === TAX_READY_EXCLUDES.length;

  return (
    <div className="p-8 max-w-7xl">
      <div className="mb-4"><Link to="/app/financials" className="text-sm text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1"><ArrowLeft className="w-4 h-4"/> Financials</Link></div>
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="text-xs font-mono-accent uppercase tracking-widest text-[#FF5A00] mb-1">Financials</div>
          <h1 className="font-heading text-3xl font-bold flex items-center gap-3"><FileBarChart className="w-7 h-7 text-[#FF5A00]"/> Statements</h1>
          <div className="text-xs text-zinc-500 mt-1">Generate monthly, quarterly or full-year snapshots. Optionally exclude payment types for cleaner filings.</div>
        </div>
      </div>

      {/* Generator card */}
      <div className="bg-white border border-zinc-200 rounded-xl card-crisp p-4 mb-6" data-testid="stmt-generator">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Monthly */}
          <div className="border border-zinc-100 rounded-lg p-3">
            <div className="text-[10px] uppercase font-mono-accent tracking-widest text-zinc-500 mb-2 flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5"/> Monthly</div>
            <div className="flex items-center gap-2 flex-wrap">
              <Select value={String(genMonth)} onValueChange={(v)=>setGenMonth(Number(v))}>
                <SelectTrigger className="w-32" data-testid="stmt-month-select"><SelectValue/></SelectTrigger>
                <SelectContent>{MONTH_NAMES.slice(1).map((n, i) => <SelectItem key={i+1} value={String(i+1)}>{n}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={String(genYear)} onValueChange={(v)=>setGenYear(Number(v))}>
                <SelectTrigger className="w-24" data-testid="stmt-year-select"><SelectValue/></SelectTrigger>
                <SelectContent>{years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
              </Select>
              <Button onClick={generateMonthly} disabled={busy} className="bg-[#FF5A00] hover:bg-[#E04F00] text-white" size="sm" data-testid="stmt-generate-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : "Generate"}
              </Button>
            </div>
          </div>
          {/* Quarterly + Yearly */}
          <div className="border border-zinc-100 rounded-lg p-3">
            <div className="text-[10px] uppercase font-mono-accent tracking-widest text-zinc-500 mb-2 flex items-center gap-1.5"><CalendarDays className="w-3.5 h-3.5"/> Quarterly / Yearly</div>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="inline-flex rounded-md border border-zinc-200 overflow-hidden">
                {[1,2,3,4].map((q) => (
                  <button
                    key={q}
                    onClick={() => setGenQuarter(q)}
                    className={`px-2.5 py-1.5 text-xs font-medium border-r border-zinc-200 last:border-r-0 ${genQuarter === q ? "bg-zinc-900 text-white" : "bg-white text-zinc-700 hover:bg-zinc-50"}`}
                    data-testid={`stmt-quarter-${q}`}
                  >Q{q}</button>
                ))}
              </div>
              <Button onClick={generateQuarterly} disabled={busy} className="bg-zinc-900 hover:bg-zinc-800 text-white" size="sm" data-testid="stmt-generate-quarterly-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <><CalendarDays className="w-4 h-4 mr-1"/> Quarterly</>}
              </Button>
              <Button onClick={generateYearly} disabled={busy} variant="outline" className="border-[#FF5A00] text-[#FF5A00] hover:bg-orange-50" size="sm" data-testid="stmt-generate-yearly-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <><CalendarRange className="w-4 h-4 mr-1"/> Yearly ({genYear === now.getFullYear() ? "YTD" : genYear})</>}
              </Button>
            </div>
          </div>
        </div>

        <div className="mt-4 pt-4 border-t border-zinc-100">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <Filter className="w-4 h-4 text-zinc-400"/>
            <div className="text-[10px] uppercase font-mono-accent tracking-widest text-zinc-500">Exclude payment types</div>
            <button
              onClick={applyTaxPreset}
              className={`ml-2 text-[11px] px-2.5 py-1 rounded-full border inline-flex items-center gap-1 transition-colors ${isTaxPresetActive ? "bg-amber-50 text-amber-800 border-amber-300" : "bg-white text-amber-700 border-amber-200 hover:bg-amber-50"}`}
              data-testid="stmt-tax-ready-preset-btn"
              title="Auto-excludes Cash, Cash App, and Venmo — the classic accountant view."
            >
              <Sparkles className="w-3 h-3"/> Tax-Ready preset {isTaxPresetActive && "✓"}
            </button>
            {excluded.length > 0 && (
              <button onClick={() => setExcluded([])} className="text-[10px] text-rose-500 hover:underline ml-auto" data-testid="stmt-clear-exclusions-btn">Clear</button>
            )}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {PAYMENT_METHOD_OPTIONS.map((m) => {
              const on = excluded.includes(m.v);
              return (
                <button
                  key={m.v}
                  onClick={() => toggleExclude(m.v)}
                  className={`text-xs px-3 py-1.5 rounded-md border inline-flex items-center gap-1.5 transition-colors ${on ? "bg-rose-600 text-white border-rose-600 line-through" : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-300"}`}
                  data-testid={`stmt-exclude-${m.v}`}
                >{m.l}</button>
              );
            })}
          </div>
          {excluded.length > 0 && (
            <div className="mt-2 text-[11px] text-zinc-500 italic">
              The generated statement will hide any invoices paid via {excluded.map((x) => PAYMENT_METHOD_LABELS[x]).join(", ")}. The exclusion is not shown on the document itself.
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <div className="lg:col-span-1 bg-white border border-zinc-200 rounded-xl card-crisp overflow-hidden">
          <div className="p-3 border-b border-zinc-100 text-xs uppercase font-mono-accent tracking-widest text-zinc-500">History</div>
          {loading ? <div className="p-6 text-center text-zinc-500"><Loader2 className="w-5 h-5 animate-spin inline"/></div>
          : items.length === 0 ? <div className="p-6 text-center text-zinc-400 text-sm">No statements yet.</div>
          : (
            <div className="divide-y divide-zinc-100 max-h-[500px] overflow-y-auto">
              {items.map((s) => (
                <div key={s.id} onClick={()=>setSelected(s)} className={`p-3 cursor-pointer hover:bg-zinc-50 flex items-center justify-between ${selected?.id === s.id ? "bg-orange-50 border-l-2 border-l-[#FF5A00]" : ""}`} data-testid={`stmt-list-item-${s.id}`}>
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate flex items-center gap-1.5">
                      {s.period === "yearly" && <CalendarRange className="w-3.5 h-3.5 text-[#FF5A00] shrink-0"/>}
                      {s.period === "quarterly" && <CalendarDays className="w-3.5 h-3.5 text-zinc-700 shrink-0"/>}
                      {statementLabel(s)}
                    </div>
                    <div className="text-xs text-zinc-500 font-mono-accent">{money(s.data?.gross_revenue)} rev</div>
                  </div>
                  <button onClick={(e)=>{e.stopPropagation(); del(s.id);}} className="p-1 rounded hover:bg-rose-50 text-rose-500"><Trash2 className="w-3.5 h-3.5"/></button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="lg:col-span-3 bg-white border border-zinc-200 rounded-xl card-crisp p-6" data-testid="stmt-detail-panel">
          {!selected ? (
            <div className="text-center text-zinc-400 py-12">Select or generate a statement.</div>
          ) : (
            <>
              <div className="flex items-start justify-between mb-6 flex-wrap gap-2">
                <div>
                  <div className="text-[10px] uppercase font-mono-accent tracking-widest text-zinc-400">
                    {isYearly ? "Yearly statement" : isQuarterly ? "Quarterly statement" : "Monthly statement"}
                  </div>
                  <h2 className="font-heading text-2xl font-bold">
                    {isYearly ? (
                      <>
                        {selected.year}
                        {selected.year === now.getFullYear() && <span className="text-zinc-400 text-sm font-normal ml-2">YTD</span>}
                      </>
                    ) : isQuarterly ? (
                      <>Q{selected.quarter} {selected.year}</>
                    ) : (
                      <>{MONTH_NAMES[selected.month]} {selected.year}</>
                    )}
                  </h2>
                  <div className="text-xs text-zinc-500">Generated {selected.generated_at?.slice(0, 10)}</div>
                  {selected.last_emailed_to && (
                    <div className="text-[11px] text-emerald-600 mt-1 inline-flex items-center gap-1">
                      <Mail className="w-3 h-3"/> Last emailed to {selected.last_emailed_to} on {selected.last_emailed_at?.slice(0, 10)}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setEmailOpen(true)} data-testid="stmt-email-btn">
                    <Send className="w-4 h-4 mr-1"/> Email statement
                  </Button>
                  <a href={pdfUrl(selected.id)} target="_blank" rel="noreferrer" download>
                    <Button className="bg-[#FF5A00] hover:bg-[#E04F00] text-white" size="sm" data-testid="stmt-download-btn"><Download className="w-4 h-4 mr-1"/> Download PDF</Button>
                  </a>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                <Stat label="Revenue" value={money(selected.data?.gross_revenue)} color="text-emerald-600"/>
                <Stat label="Expenses" value={money(selected.data?.total_expenses)} color="text-rose-600"/>
                <Stat label="Net Income" value={money(selected.data?.net_income)} color={selected.data?.net_income >= 0 ? "text-emerald-600" : "text-rose-600"}/>
                <Stat label="Margin" value={`${selected.data?.net_to_gross_pct ?? 0}%`}/>
              </div>

              {selected.data?.mom && !isYearly && !isQuarterly && (
                <div className="mb-6">
                  <div className="font-heading font-bold text-sm mb-2">Month-over-Month</div>
                  <ComparisonTable prevLabel={`${MONTH_NAMES[selected.data.mom.prev_month?.month] || ""}`} curLabel="This month"
                    data={selected.data} deltas={selected.data.mom} prev={selected.data.mom.prev_month}/>
                </div>
              )}

              {selected.data?.qoq && isQuarterly && (
                <div className="mb-6">
                  <div className="font-heading font-bold text-sm mb-2">Quarter-over-Quarter</div>
                  <ComparisonTable
                    prevLabel={`Q${selected.data.qoq.prev_quarter?.quarter} ${selected.data.qoq.prev_quarter?.year}`}
                    curLabel={`Q${selected.quarter} ${selected.year}`}
                    data={selected.data} deltas={selected.data.qoq} prev={selected.data.qoq.prev_quarter}/>
                </div>
              )}

              {selected.data?.yoy && isYearly && (
                <div className="mb-6">
                  <div className="font-heading font-bold text-sm mb-2">Year-over-Year</div>
                  <ComparisonTable prevLabel={`${selected.data.yoy.prev_year?.year || ""}`} curLabel={`${selected.year}${selected.year === now.getFullYear() ? " YTD" : ""}`}
                    data={selected.data} deltas={selected.data.yoy} prev={selected.data.yoy.prev_year}/>
                </div>
              )}

              {selected.data?.expense_categories && Object.keys(selected.data.expense_categories).length > 0 && (
                <div className="mb-6">
                  <div className="font-heading font-bold text-sm mb-2">Expense Breakdown</div>
                  <div className="space-y-2">
                    {Object.entries(selected.data.expense_categories).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
                      <div key={k} className="flex items-center justify-between text-sm">
                        <span>{k}</span>
                        <span className="font-mono-accent">{money(v)} · {selected.data.expense_category_pct_of_expenses?.[k]?.toFixed(1) ?? "0"}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selected.data?.ytd && !isYearly && !isQuarterly && (
                <div className="pt-4 border-t border-zinc-100 text-sm text-zinc-600">
                  <b>YTD:</b> Revenue {money(selected.data.ytd.gross_revenue)} · Expenses {money(selected.data.ytd.total_expenses)} · Net {money(selected.data.ytd.net_income)}
                </div>
              )}

              <div className="mt-6 pt-4 border-t border-zinc-100 text-[11px] text-zinc-400 italic">This statement is generated by LeadForge and is not a substitute for professional accounting or tax advice.</div>
            </>
          )}
        </div>
      </div>

      {selected && (
        <EmailStatementDialog
          statement={selected}
          open={emailOpen}
          onOpenChange={setEmailOpen}
          onSent={() => { setEmailOpen(false); load(); }}
        />
      )}
    </div>
  );
}

function ComparisonTable({ prevLabel, curLabel, data, deltas, prev }) {
  return (
    <table className="w-full text-sm">
      <thead className="text-xs uppercase font-mono-accent text-zinc-500">
        <tr><th className="text-left pb-2"></th><th className="text-right pb-2">{prevLabel}</th><th className="text-right pb-2">{curLabel}</th><th className="text-right pb-2">Change</th></tr>
      </thead>
      <tbody>
        <tr><td className="py-1">Revenue</td><td className="text-right font-mono-accent">{money(prev?.gross_revenue)}</td><td className="text-right font-mono-accent">{money(data.gross_revenue)}</td><td className="text-right font-mono-accent">{money(deltas.revenue_change)} ({deltas.revenue_pct ?? "—"}%)</td></tr>
        <tr><td className="py-1">Expenses</td><td className="text-right font-mono-accent">{money(prev?.total_expenses)}</td><td className="text-right font-mono-accent">{money(data.total_expenses)}</td><td className="text-right font-mono-accent">{money(deltas.expenses_change)} ({deltas.expenses_pct ?? "—"}%)</td></tr>
        <tr><td className="py-1">Net income</td><td className="text-right font-mono-accent">{money(prev?.net_income)}</td><td className="text-right font-mono-accent">{money(data.net_income)}</td><td className="text-right font-mono-accent">{money(deltas.net_change)} ({deltas.net_pct ?? "—"}%)</td></tr>
      </tbody>
    </table>
  );
}

function Stat({ label, value, color = "text-zinc-900" }) {
  return (
    <div className="bg-zinc-50 rounded-lg p-3">
      <div className="text-[10px] uppercase font-mono-accent text-zinc-500">{label}</div>
      <div className={`text-lg font-bold ${color}`}>{value}</div>
    </div>
  );
}

function EmailStatementDialog({ statement, open, onOpenChange, onSent }) {
  const [to, setTo] = useState(statement?.last_emailed_to || "");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (open) {
      setTo(statement?.last_emailed_to || "");
      setSubject("");
      setMessage("");
    }
  }, [open, statement]);

  const submit = async (e) => {
    e.preventDefault();
    if (!to.trim() || !/^\S+@\S+\.\S+$/.test(to.trim())) { toast.error("Enter a valid email"); return; }
    setSending(true);
    try {
      const { data } = await api.post(`/financials/statements/${statement.id}/email`, { to: to.trim(), subject: subject.trim() || undefined, message: message.trim() || undefined });
      toast.success(`Statement emailed to ${to.trim()}`);
      onSent?.(data);
    } catch (err) {
      const detail = err.response?.data?.detail || "Failed to send email";
      // Preview Resend sandbox rejects unverified recipients — offer the
      // secure download URL as a copy-and-share fallback so the admin isn't
      // stuck. This falls out cleanly in Production where all domains work.
      toast.error(detail, { duration: 8000 });
    }
    finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="stmt-email-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl flex items-center gap-2"><Mail className="w-5 h-5 text-[#FF5A00]"/> Email statement</DialogTitle>
          <DialogDescription>
            Sends a branded email summary with a secure PDF download link. The recipient does not need a LeadForge account.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label>Recipient email</Label>
            <Input type="email" required value={to} onChange={(e) => setTo(e.target.value)} placeholder="accountant@firm.com" data-testid="stmt-email-to-input" autoFocus/>
          </div>
          <div>
            <Label>Subject <span className="text-zinc-400 font-normal">(optional)</span></Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Auto-fills with statement title if left blank" data-testid="stmt-email-subject-input"/>
          </div>
          <div>
            <Label>Message <span className="text-zinc-400 font-normal">(optional)</span></Label>
            <Textarea rows={4} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Hi Sam, here's the Q3 statement for your review…" data-testid="stmt-email-message-input"/>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={sending} className="bg-[#FF5A00] hover:bg-[#E04F00] text-white" data-testid="stmt-email-send-btn">
              {sending ? <><Loader2 className="w-4 h-4 mr-1 animate-spin"/> Sending…</> : <><Send className="w-4 h-4 mr-1"/> Send</>}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
